'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  AvailabilityHeader,
  AvailabilityTab
} from '@/components/dashboard/availability/AvailabilityHeader';
import {
  StaffScheduleCards,
  StaffItem,
  ScheduleItem
} from '@/components/dashboard/availability/StaffScheduleCards';
import { StaffDrawer } from '@/components/dashboard/availability/StaffDrawer';
import { ResourcesTab } from '@/components/dashboard/availability/ResourcesTab';
import { TimeBlocksTab } from '@/components/dashboard/availability/TimeBlocksTab';
import { Users2, CalendarDays } from 'lucide-react';

export default function AvailabilityPage() {
  const [activeTab, setActiveTab] = useState<AvailabilityTab>('horarios');
  const [staffList, setStaffList] = useState<StaffItem[]>([]);
  const [servicesList, setServicesList] = useState<{ id: string; name: string; duration: number; cashPrice: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStaffDrawer, setShowStaffDrawer] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffItem | null>(null);

  const fetchStaffData = useCallback(async () => {
    try {
      const [resStaff, resServices] = await Promise.all([
        fetch('/api/staff'),
        fetch('/api/services')
      ]);
      const dataStaff = await resStaff.json();
      const dataServices = await resServices.json();

      if (dataStaff.success) {
        setStaffList(dataStaff.staff || []);
      }
      if (dataServices.success) {
        setServicesList(dataServices.services || []);
      }
    } catch (err) {
      console.error('Error fetching staff/services in availability:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaffData();
  }, [fetchStaffData]);

  // Toggle activo/inactivo para profesional
  const handleToggleStaffActive = async (staffId: string, currentActive: boolean) => {
    try {
      const res = await fetch('/api/staff', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: staffId, active: !currentActive })
      });
      const data = await res.json();
      if (data.success) {
        setStaffList(prev =>
          prev.map(s => (s.id === staffId ? { ...s, active: !currentActive } : s))
        );
      }
    } catch (err) {
      alert('Error al actualizar estado del profesional');
    }
  };

  // Toggle destacado/sugerido para profesional
  const handleToggleStaffFeatured = async (staffId: string, currentFeatured: boolean) => {
    try {
      const res = await fetch('/api/staff', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: staffId, isFeatured: !currentFeatured })
      });
      const data = await res.json();
      if (data.success) {
        setStaffList(prev =>
          prev.map(s => (s.id === staffId ? { ...s, isFeatured: !currentFeatured } : s))
        );
      }
    } catch (err) {
      alert('Error al actualizar profesional destacado');
    }
  };

  // Guardar horarios de un profesional
  const handleSaveStaffSchedules = async (staffId: string, schedules: ScheduleItem[]) => {
    try {
      const res = await fetch('/api/staff', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: staffId,
          schedules: schedules.map(sc => ({
            id: sc.id,
            enabled: sc.enabled,
            startTime: sc.startTime,
            endTime: sc.endTime,
            breakStart: sc.breakStart || null,
            breakEnd: sc.breakEnd || null
          }))
        })
      });
      const data = await res.json();
      if (data.success) {
        fetchStaffData();
      } else {
        alert(data.error || 'Error al guardar horarios');
      }
    } catch (err) {
      alert('Error de conexión');
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header with 5 Tabs */}
      <AvailabilityHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenNewStaff={() => {
          setEditingStaff(null);
          setShowStaffDrawer(true);
        }}
      />

      {/* Main Tab Content */}
      {loading ? (
        <div className="bg-white border border-slate-200/80 rounded-2xl p-12 text-center text-xs text-slate-400 font-medium">
          Cargando configuración de horarios y profesionales...
        </div>
      ) : (
        <>
          {activeTab === 'horarios' && (
            <StaffScheduleCards
              staffList={staffList}
              onToggleStaffActive={handleToggleStaffActive}
              onToggleStaffFeatured={handleToggleStaffFeatured}
              onSaveStaffSchedules={handleSaveStaffSchedules}
              onEditStaff={(staff) => {
                setEditingStaff(staff);
                setShowStaffDrawer(true);
              }}
            />
          )}

          {activeTab === 'recursos' && <ResourcesTab />}

          {activeTab === 'equipos' && (
            <div className="bg-white border border-slate-200/80 rounded-2xl p-8 text-center space-y-3">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-700 rounded-2xl flex items-center justify-center mx-auto">
                <Users2 className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-900">Equipos y Turnos Rotativos</h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Todos los profesionales ({staffList.length}) operan actualmente bajo la dotación principal del salón.
              </p>
            </div>
          )}

          {activeTab === 'bloqueos' && (
            <TimeBlocksTab staffList={staffList.map(s => ({ id: s.id, name: s.name }))} />
          )}

          {activeTab === 'especiales' && (
            <div className="bg-white border border-slate-200/80 rounded-2xl p-8 text-center space-y-3">
              <div className="w-12 h-12 bg-amber-50 text-amber-700 rounded-2xl flex items-center justify-center mx-auto">
                <CalendarDays className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-900">Días Especiales & Feriados</h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Configuración de jornadas especiales con horarios reducidos o extendidos para fechas festivas.
              </p>
            </div>
          )}
        </>
      )}

      {/* Slide-Over Drawer: Staff */}
      <StaffDrawer
        isOpen={showStaffDrawer}
        onClose={() => {
          setShowStaffDrawer(false);
          setEditingStaff(null);
        }}
        staff={editingStaff}
        servicesList={servicesList}
        onSaved={fetchStaffData}
      />
    </div>
  );
}
